/**
 * 面板布局状态管理 Store
 *
 * 管理侧边栏、面板布局、灵枢工作台、临床场景等与工作区布局相关的状态。
 * 从 uiStore.ts 拆分而来（P2-1 巨型 Store 拆解）。
 *
 * @module shared/stores/layoutStore
 */

import { defineStore } from "pinia";
import { ref, reactive, computed, type Ref, type ComputedRef } from "vue";
import { shouldActivateEmbedShell } from "@shared/utils/embedShellActivation";
import {
  persistDisplayMode,
  readPersistedDisplayMode,
  requiredCapabilityForMode,
  resolveEntitledDisplayMode,
  resolveInitialDisplayMode,
  type DisplayMode,
} from "@shared/utils/displayMode";
import { useCapabilityStore } from "./capabilityStore";

// 兼容旧引用：DisplayMode 权威定义在 shared/utils/displayMode，本模块继续透传
export type { DisplayMode } from "@shared/utils/displayMode";

// ==================== 类型定义 ====================

/** 布局配置类型 */
export interface LayoutConfig {
  headerHeight: number;
  footerHeight: number;
  sidebarMinWidth: number;
  sidebarMaxWidth: number;
}

/**
 * 工作站类型：HIS 登录工作站落地前的过渡态；急诊工具已改为门诊的 emergency 模块插件。
 * 场景与模块权益的装配声明在 `src/config/workstations.ts`（工作站注册表）。
 */
export type ClinicalScene = "outpatient" | "inpatient" | "nurse";

// ==================== 工具函数 ====================

/**
 * 采集环境来源并解析初始显示模式（优先级见 displayMode.resolveInitialDisplayMode）。
 * 本模块只负责状态与存储偏好；跨工作区导航统一由路由层负责，不在此处做路由跳转。
 */
function readInitialDisplayMode(): DisplayMode {
  let embedShellActive = false;
  try {
    embedShellActive = shouldActivateEmbedShell();
  } catch {
    /* 环境探测失败按非嵌入处理 */
  }
  // 已下线段控的模式不再作为持久化恢复项：「文书」入口迁至独立病历书写系统。
  // 深链 ?mode=workspace 仍由路由层解析，这里只处理本地存储偏好。
  const storedMode = readPersistedDisplayMode();
  return resolveInitialDisplayMode({
    embedShellActive,
    storedMode: storedMode === "workspace" ? null : storedMode,
  });
}

/** 存储恢复时需先校验能力授权的临床内模式（teach 走路由守卫，不在此列） */
const DEFERRED_RESTORE_MODES: ReadonlySet<DisplayMode> = new Set([
  "workspace",
  "diagnosis",
  "order",
]);

/**
 * 初始模式拆分：需授权的存储偏好先以 full 呈现，待权益目录加载后再恢复，
 * 避免未开通机构在校验完成前渲染工作台（默认拒绝）。
 */
function splitInitialDisplayMode(): {
  initial: DisplayMode;
  deferred: DisplayMode | null;
} {
  const mode = readInitialDisplayMode();
  return DEFERRED_RESTORE_MODES.has(mode)
    ? { initial: "full", deferred: mode }
    : { initial: mode, deferred: null };
}

// ==================== Store 定义 ====================

export const usePanelLayoutStore = defineStore("panelLayout", () => {
  // ==================== 侧边栏状态 ====================

  const sidebarCollapsed: Ref<boolean> = ref(false);
  const sidebarWidth: Ref<number> = ref(300);

  // ==================== 面板布局控制状态 ====================

  const leftPanelVisible: Ref<boolean> = ref(true);
  const rightPanelVisible: Ref<boolean> = ref(true);
  const panelsSwapped: Ref<boolean> = ref(false);

  // ==================== 显示模式状态 ====================

  const initialMode = splitInitialDisplayMode();
  const displayMode: Ref<DisplayMode> = ref(initialMode.initial);
  /** 待授权校验后恢复的存储偏好（null 表示无待恢复项） */
  let deferredDisplayMode: DisplayMode | null = initialMode.deferred;
  const isWorkspaceMode = computed(() => displayMode.value === "workspace");
  const isDiagnosisMode = computed(() => displayMode.value === "diagnosis");
  const isOrderMode = computed(() => displayMode.value === "order");
  const isPluginMode = computed(() => displayMode.value === "plugin");
  const isEmbedMode = computed(() => displayMode.value === "embed");
  const isTeachMode = computed(() => displayMode.value === "teach");

  // ==================== 工作站状态 ====================

  const clinicalScene: Ref<ClinicalScene> = ref("outpatient");
  /** 当前机构已开通的工作站集合（门诊始终可见，住院/护士站按模块授权控制） */
  const enabledScenes: Ref<Set<string>> = ref(new Set(["outpatient"]));
  const isInpatientMode = computed(() => clinicalScene.value === "inpatient");
  const isNurseMode = computed(() => clinicalScene.value === "nurse");

  // ==================== 布局设置 ====================

  const layout = reactive<LayoutConfig>({
    headerHeight: 60,
    footerHeight: 50,
    sidebarMinWidth: 200,
    sidebarMaxWidth: 600,
  });

  // ==================== 计算属性 ====================

  /** 侧边栏实际宽度 */
  const actualSidebarWidth: ComputedRef<number> = computed(() => {
    return sidebarCollapsed.value ? 64 : sidebarWidth.value;
  });

  // ==================== 侧边栏操作方法 ====================

  /** 切换侧边栏展开/收起状态 */
  const toggleSidebar = (): void => {
    sidebarCollapsed.value = !sidebarCollapsed.value;
  };

  /** 设置侧边栏宽度 */
  const setSidebarWidth = (width: number): void => {
    const minWidth = layout.sidebarMinWidth;
    const maxWidth = layout.sidebarMaxWidth;

    if (width >= minWidth && width <= maxWidth) {
      sidebarWidth.value = width;
    }
  };

  // ==================== 显示模式操作方法 ====================

  /** 设置显示模式：全屏主模式 / 灵枢工作台 / 医嘱工作台 / 插件模式 / HIS 嵌入伴随小窗 / 教学自学 */
  const setDisplayMode = (mode: DisplayMode): void => {
    // 任何显式模式设置都以最新意图为准，放弃尚未完成的存储偏好恢复
    deferredDisplayMode = null;
    displayMode.value = mode;
    // embed 是宿主 HIS 通过 URL 激活的临时态，persistDisplayMode 内部改为清除存储，
    // 避免污染独立访问时的默认模式；存储不可用（隐私模式）时静默忽略。
    // 不在此处做路由跳转：跨工作区导航由路由层 / useWorkspaceNavigation 负责。
    persistDisplayMode(mode);
    if (mode === "full") {
      leftPanelVisible.value = true;
      rightPanelVisible.value = true;
    }
  };

  /** 切换灵枢工作台 */
  const toggleWorkspaceMode = (value?: boolean): void => {
    if (typeof value === "boolean") {
      setDisplayMode(value ? "workspace" : "full");
    } else {
      setDisplayMode(isWorkspaceMode.value ? "full" : "workspace");
    }
  };

  /** 确保权益目录已加载；失败保持默认拒绝并返回 false */
  const ensureCapabilitiesLoaded = async (): Promise<boolean> => {
    const capabilityStore = useCapabilityStore();
    if (capabilityStore.loaded) return true;
    try {
      await capabilityStore.load();
      return true;
    } catch (err) {
      console.warn("⚠️ 加载授权目录失败，需授权的模式按未开通处理", err);
      return false;
    }
  };

  /**
   * 经能力门控进入模式（URL 参数、外部 window 钩子等非顶栏入口统一走这里）：
   * 已开通 → 进入目标模式；未开通或目录加载失败 → 回退 full。
   * @returns 是否实际进入了目标模式
   */
  const applyEntitledDisplayMode = async (
    mode: DisplayMode,
  ): Promise<boolean> => {
    deferredDisplayMode = null;
    if (requiredCapabilityForMode(mode)) await ensureCapabilitiesLoaded();
    const capabilityStore = useCapabilityStore();
    const resolved = resolveEntitledDisplayMode(mode, capabilityStore.has);
    setDisplayMode(resolved);
    return resolved === mode;
  };

  /**
   * 恢复被延后的存储偏好：目录加载失败（如未登录）时保留偏好待下次重试；
   * 加载成功但未开通时清除偏好；期间用户已主动切换模式则放弃恢复。
   */
  const restoreDeferredDisplayMode = async (): Promise<void> => {
    const mode = deferredDisplayMode;
    if (!mode) return;
    if (!(await ensureCapabilitiesLoaded())) return;
    if (deferredDisplayMode !== mode) return;
    deferredDisplayMode = null;
    if (displayMode.value !== "full") return;
    const capabilityStore = useCapabilityStore();
    setDisplayMode(resolveEntitledDisplayMode(mode, capabilityStore.has));
  };

  /** URL 显式指定模式时放弃存储偏好的延后恢复 */
  const discardDeferredDisplayMode = (): void => {
    deferredDisplayMode = null;
  };

  // ==================== 面板布局控制方法 ====================

  /** 切换左侧面板显示状态 */
  const toggleLeftPanel = (): void => {
    leftPanelVisible.value = !leftPanelVisible.value;
  };

  /** 切换右侧面板显示状态 */
  const toggleRightPanel = (): void => {
    rightPanelVisible.value = !rightPanelVisible.value;
  };

  /** 交换中间面板和右侧面板位置 */
  const swapPanels = (): void => {
    panelsSwapped.value = !panelsSwapped.value;
  };

  /** 设置左侧面板显示状态 */
  const setLeftPanelVisible = (visible: boolean): void => {
    leftPanelVisible.value = visible;
  };

  /** 设置右侧面板显示状态 */
  const setRightPanelVisible = (visible: boolean): void => {
    rightPanelVisible.value = visible;
  };

  /** 重置面板布局为默认状态 */
  const resetPanelLayout = (): void => {
    leftPanelVisible.value = true;
    rightPanelVisible.value = true;
    panelsSwapped.value = false;
  };

  // ==================== 临床场景操作方法 ====================

  /** 切换临床场景 */
  const setClinicalScene = (scene: ClinicalScene): void => {
    clinicalScene.value = scene;
  };

  /** 从统一 Entitlement 投影加载工作站；急诊工具由 emergency 模块在门诊工具栏授权，不再作为场景。 */
  const initEnabledScenes = async (): Promise<void> => {
    const scenes = new Set<string>(["outpatient"]); // 门诊始终可见
    const capabilityStore = useCapabilityStore();
    try {
      await capabilityStore.load();
    } catch (err) {
      console.warn("⚠️ 加载场景能力失败，仅显示门诊模式", err);
    }
    if (capabilityStore.has("inpatient")) {
      scenes.add("inpatient");
    }
    // 护士工作站独立授权：只要求 nursing 模块，不要求 inpatient；
    // 角色是否合适由工作站注册表（roleHints）在入口处二次过滤。
    if (capabilityStore.has("nursing")) {
      scenes.add("nurse");
    }
    enabledScenes.value = scenes;
    // 如果当前场景不在已授权集合中，回退到门诊
    if (!scenes.has(clinicalScene.value)) {
      clinicalScene.value = "outpatient";
    }
  };

  return {
    // 侧边栏状态
    sidebarCollapsed,
    sidebarWidth,
    actualSidebarWidth,

    // 面板布局状态
    leftPanelVisible,
    rightPanelVisible,
    panelsSwapped,

    // 显示模式状态
    displayMode,
    isWorkspaceMode,
    isDiagnosisMode,
    isOrderMode,
    isPluginMode,
    isEmbedMode,
    isTeachMode,

    // 工作站状态
    clinicalScene,
    enabledScenes,
    isInpatientMode,
    isNurseMode,

    // 布局配置
    layout,

    // 侧边栏方法
    toggleSidebar,
    setSidebarWidth,

    // 显示模式方法
    setDisplayMode,
    toggleWorkspaceMode,
    applyEntitledDisplayMode,
    restoreDeferredDisplayMode,
    discardDeferredDisplayMode,

    // 面板布局方法
    toggleLeftPanel,
    toggleRightPanel,
    swapPanels,
    setLeftPanelVisible,
    setRightPanelVisible,
    resetPanelLayout,

    // 工作站方法
    setClinicalScene,
    initEnabledScenes,
  };
});
